// ── THE FARM ELECTION SUMMARY, AS A PAGE ─────────────────────────────────────────────────
// Renders buildFarmElectionSummary's result. It PRINTS; it does not calculate — every figure
// below is a field of the summary, formatted. A sentence here may choose words for a number,
// never compute one.
//
// ⚠ THE WORDS ARE HELD TO WHAT THE ARITHMETIC SAYS. Where a dividing payment yield exists, above
// it PLC's modeled payment is at least as large as ARC-CO's at EVERY price; below it ARC-CO's is
// larger over PART of the price range (it pays first as prices fall and caps; PLC keeps paying
// in a collapse). "ARC-CO below" without that qualifier would be a stronger claim than the
// comparison supports, and this is a page a grower signs an election after reading.

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const n = (v) => (v == null || !isFinite(Number(v)) ? null : Number(v));
const d2 = (v) => (n(v) == null ? "—" : "$" + n(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const buAc = (v) => (n(v) == null ? "—" : Math.round(n(v)) + " bu/ac");
const title = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const date = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? MONTHS[+m[2] - 1] + " " + +m[3] + ", " + m[1] : "—";
};
const cropName = (c) => title(c);

// The printable area of a landscape letter sheet at the @page margins below, and the range the
// page may be scaled over to land on exactly one sheet.
export const PRINT_W_IN = 10.44;   // 11in − 2 × 0.28in
export const PRINT_H_IN = 7.9;     // 8.5in − 2 × 0.25in, less a tenth for the printer's rounding
export const FIT_MAX = 1.35;
export const FIT_MIN = 0.72;

// ── THE COMPARISON SENTENCE — one wording, two surfaces ──────────────────────────────────────
// The farm one-pager and the Coverage Advisor panel print the SAME sentence for a county, off the
// same countyCell, so they can never describe one county two ways (Kevin, 2026-09-19). It is the
// comparison AT BENCHMARK COUNTY YIELD, and it does not depend on USDA's projected MYA being low
// enough for either program to pay — the farmer elects either way.
// ⚠ BELOW THE DIVIDING PAYMENT YIELD, ARC-CO IS LARGER OVER PART OF THE PRICE RANGE, NOT AT EVERY
// PRICE: PLC still pays first between the ARC-CO trigger and the reference price. So the sentence
// is qualified; "ARC-CO produces the larger modeled payment below it" would be a stronger claim
// than the arithmetic supports.
// `b` wraps a figure (bold in HTML, plain for React). Returns null where nothing can be said.
export function comparisonSentence(cell, b = (x) => x) {
  const c = cell && cell.comparison;
  if (!cell || !cell.usable || !c || !c.usable) return null;
  if (c.paymentYield != null) {
    if (c.kind === "threshold")
      return `At benchmark county yield and your ${b(buAc(c.paymentYield))} FSA PLC payment yield: ${c.paymentYield >= c.switchYield
        ? `PLC's modeled payment is at least as large as ARC-CO's at every price (the dividing payment yield is ${b(buAc(c.switchYield))}).`
        : `it is below the ${b(buAc(c.switchYield))} dividing payment yield — ${c.belowSwitchAtPaymentYield || c.belowSwitch}.`}`;
    if (c.kind === "arcFirst" && c.plcLargerBelowAtPaymentYield != null)
      return `At benchmark county yield and your ${b(buAc(c.paymentYield))} FSA PLC payment yield: ARC-CO begins paying first as prices fall; PLC's modeled payment is larger only if the MYA falls below ${b(d2(c.plcLargerBelowAtPaymentYield))}.`;
  }
  if (c.kind === "threshold")
    return `At benchmark county yield, PLC produces the larger modeled payment above a ${b(buAc(c.switchYield))} FSA PLC payment yield; below it, ${c.belowSwitch}.`;
  if (c.kind === "arcFirst")
    return `At benchmark county yield, no single payment yield divides the two: ARC-CO begins paying first as prices fall (below ${b(d2(c.arcStartsBelow))}), and PLC's modeled payment is larger only if the MYA falls below a price set by the farm's FSA PLC payment yield.`;
  return c.reason || "No single comparison is available for this county.";
}

// ── THE DECISION, IN ONE LINE PER CROP ───────────────────────────────────────────────────────
// Kevin, 2026-09-19, on the first version of this page: *"This is absolutely way too much text. I
// believe early on I said don't make it too wordy, make it simple and clear as to which election to
// make — this is not that."* So a crop gets ONE call and the ONE number that decides it; the four
// FSA figures sit beside it; every caveat is stated ONCE, in the footnote. Nothing is computed here
// — `call` and `sub` are the summary's own fields, worded.
//
// ⚠ THE QUALIFIER MOVED, IT DID NOT GO. Below the dividing payment yield ARC-CO's modeled payment
// leads over PART of the price range, not at every price. Saying that in every sentence is what made
// the page unreadable, so the decision line names the side and the footnote carries the qualifier —
// Kevin's own layout call. The RULE is unchanged: no surface may claim it at every price.
function decision(crop) {
  const us = crop.counties.filter((k) => k.usable && k.comparison && k.comparison.usable);
  if (!us.length) {
    const k0 = crop.counties[0] || {};
    return { lines: [["Not computed", esc((k0.comparison && k0.comparison.reason) || k0.reason || "no FSA figures for this crop")]], sub: "" };
  }
  const kinds = [...new Set(us.map((k) => k.comparison.kind))];
  const py = us[0].comparison.paymentYield;
  // The dividing payment yield exists (PLC pays first): the call is the grower's own FSA figure
  // against that line — one number where the counties agree, the odd county named where they do not.
  if (kinds.length === 1 && kinds[0] === "threshold") {
    const marks = us.map((k) => ({ county: title(k.county), y: Math.round(k.comparison.switchYield) }));
    const lo = Math.min(...marks.map((m) => m.y)), hi = Math.max(...marks.map((m) => m.y));
    const line = `${hi} bu`;
    const lower = marks.filter((m) => m.y < hi);
    const exceptions = lower.length ? ` · ${lower.map((m) => m.county + "'s line is " + m.y + " bu").join(" · ")}` : "";
    if (py != null) {
      const above = py >= hi, below = py < lo;
      return { lines: [[above ? "PLC" : below ? "ARC-CO" : "By county",
                        `your <b>${buAc(py)}</b> FSA payment yield is ${above ? "above" : below ? "below" : "between the counties'"} the ${line} line`]],
               sub: exceptions.replace(/^ · /, "") };
    }
    // ⚠ TWO "IF" LINES ARE NOT AN ANSWER, AND THIS CONDITION IS KNOWABLE (Kevin, 2026-09-23:
    // "Just tell me what to enroll in"). Unlike the price, the FSA PLC payment yield is a number
    // the grower already holds — it is on the FSA farm record. So the row asks for the one input
    // that settles it rather than printing a fork the reader has to resolve. Nothing is assumed:
    // no payment yield is invented here, which is the BUG-090 rule this page already carries.
    return { lines: [["Get your FSA payment yield", `PLC at <b>${line}</b> or more · ARC-CO below it`]],
             sub: (exceptions ? exceptions.replace(/^ · /, "") + " · " : "") + "it is on your FSA farm record, not your APH" };
  }
  // ── ARC-CO PAYS FIRST AS PRICES FALL: THE CALL IS MADE AT USDA'S OWN PROJECTION ──────────
  // Kevin, 2026-09-23, on "ARC-CO unless the price ends below $3.41–$3.87 · PLC if it does":
  // *"you can't say below and then have a range. And secondly we have no idea where the price
  // is going to close, so you can't say it like that. Just tell me what to enroll in."*
  //
  // Both halves were right, and the second is the substantive one. An election is signed at
  // FSA months before the marketing-year average price exists, so a sentence conditioned on
  // that price asks the grower to decide the thing we were meant to decide.
  //
  // ⚠ AND THE RANGE WAS NOT WHAT IT LOOKED LIKE. With no payment yield the band came from
  // `crop.ruler` — the crossover at 60/80/100% of the county benchmark — so it spanned
  // HYPOTHETICAL PAYMENT YIELDS, not counties. Printed after the word "below" it read as a
  // price threshold with a mysterious width.
  //
  // That same band is what makes an unconditional answer possible: where USDA's published
  // projection clears its TOP, ARC-CO is the call at every payment yield in the ruler, so the
  // number we do not hold cannot change it. The crossover stops being the sentence and becomes
  // what it always was — the price at which the call would flip.
  if (kinds.length === 1 && kinds[0] === "arcFirst") {
    const own = us.map((k) => n(k.comparison.plcLargerBelowAtPaymentYield)).filter((v) => v != null);
    const marks = (crop.ruler ? crop.ruler.counties.flatMap((c) => c.lines.map((l) => n(l.plcLargerBelow))) : []).filter((v) => v != null);
    const usingOwn = py != null && own.length > 0;
    const band = usingOwn ? own : marks;
    // USDA's own published projected MYA price, already printed in the figures beside this row.
    // Silas forecasts nothing; it reads the projection and says which side of the line it is on.
    const projs = us.map((k) => n(k.keyNumbers && k.keyNumbers.myaProjection)).filter((v) => v != null);
    const proj = projs.length ? Math.min(...projs) : null;
    const need = (why) => ({ lines: [["Get your FSA payment yield", why]],
                             sub: "it is on your FSA farm record, not your APH" });
    if (!band.length || proj == null) return need("it sets the price below which PLC overtakes ARC-CO");
    const lo = Math.min(...band), hi = Math.max(...band);
    const anyYield = usingOwn ? "" : ", whatever your payment yield";
    if (proj >= hi) {
      // Above ARC-CO's own trigger neither program pays on price at all, and saying "ARC-CO's
      // payment is larger" would imply a payment that is not modeled. ARC-CO is still the call,
      // for the reason that is actually true there: it is the only one of the two that answers a
      // county yield shortfall.
      const trigs = us.map((k) => n(k.comparison.arcStartsBelow)).filter((v) => v != null);
      const trig = trigs.length ? Math.max(...trigs) : null;
      const why = trig != null && proj > trig
        ? `PLC pays nothing at USDA's projected ${d2(proj)} · ARC-CO also covers a county yield shortfall`
        : `ARC-CO's modeled payment is the larger at USDA's projected ${d2(proj)}`;
      return { lines: [["ARC-CO", ""]], sub: `${why} · flips to PLC only below <b>${d2(hi)}</b>${anyYield}` };
    }
    if (proj < lo) {
      return { lines: [["PLC", ""]],
               sub: `USDA projects ${d2(proj)} — below the <b>${d2(lo)}</b> where PLC's modeled payment becomes the larger${anyYield}` };
    }
    // The projection lands inside the band: the payment yield genuinely decides this one, and it
    // is a number the grower holds rather than one nobody can know. Ask for it instead of hedging.
    return need(`USDA projects ${d2(proj)}, and PLC overtakes ARC-CO between <b>${d2(lo)}</b> and <b>${d2(hi)}</b> depending on that number`);
  }
  // Counties genuinely differ in kind: each says its own, briefly.
  return { lines: us.map((k) => [esc(title(k.county)),
             k.comparison.kind === "threshold" ? `PLC above <b>${buAc(k.comparison.switchYield)}</b>, ARC-CO if not` : "ARC-CO first as prices fall"]), sub: "" };
}

// The four FSA figures, which are the same in every county of a crop (the benchmark price and the
// reference price are national, and the trigger is 90% of the benchmark price). Where a crop's
// counties disagree to the cent, the range is printed rather than one county's figure.
function figures(crop) {
  const us = crop.counties.filter((k) => k.usable);
  if (!us.length) return "";
  const one = (get) => {
    const v = us.map((k) => n(get(k.keyNumbers))).filter((x) => x != null);
    if (!v.length) return "—";
    const lo = Math.min(...v), hi = Math.max(...v);
    return Math.round(lo * 100) === Math.round(hi * 100) ? d2(lo) : d2(lo) + "–" + d2(hi);
  };
  const asOf = us[0].keyNumbers.myaAsOf;
  return `<div class="fes-figs">
    <span><i>ARC benchmark</i> ${one((k) => k.arcBenchmarkPrice)}</span>
    <span><i>ARC pays below</i> ${one((k) => k.arcPriceTriggerAtBenchmarkYield)}</span>
    <span><i>PLC reference</i> ${one((k) => k.plcEffectiveReferencePrice)}</span>
    <span><i>USDA projected</i> ${one((k) => k.myaProjection)}${asOf ? ` <b class="fes-tiny">${esc(shortDate(asOf))}</b>` : ""}</span>
  </div>`;
}

const shortDate = (iso) => { const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? MONTHS[+m[2] - 1] + " " + +m[3] : "—"; };

// A county the page could not compute is NAMED with what it needs — never dropped, never averaged in.
function refusals(crop) {
  const bad = crop.counties.filter((k) => !k.usable);
  if (!bad.length) return "";
  return `<div class="fes-ref">${bad.map((k) => k.split
    ? `${esc(title(k.county))} — FSA splits this county by irrigation; add the farm's irrigated share`
    : `${esc(title(k.county))} — ${esc(k.reason || "not computed")}`).join(" · ")}</div>`;
}

function cropRow(crop) {
  const d = decision(crop);
  const us = crop.counties.filter((k) => k.usable);
  const counties = us.map((k) => title(k.county)).join(" · ");
  return `<section class="fes-row">
    <div class="fes-c1"><div class="fes-cn">${esc(cropName(crop.crop))}</div><div class="fes-cc">${esc(counties)}</div></div>
    <div class="fes-c2">${d.lines.map(([who, cond]) => `<div class="fes-opt"><b>${esc(who)}</b> <span>${cond}</span></div>`).join("")}${d.sub ? `<div class="fes-sub">${d.sub}</div>` : ""}${refusals(crop)}</div>
    <div class="fes-c3">${figures(crop)}${crop.label && crop.label.nextCrop
      ? `<div class="fes-next">${esc(crop.label.nextCrop.replace(/(\d{4})-(\d{2})-(\d{2})/g, (m) => date(m)))}</div>` : ""}</div>
  </section>`;
}

// ⚠ ONE SHEET, TWO PLACES (Kevin, 2026-09-19: "use that exact sheet in the full strategy report").
// The markup and the CSS are written ONCE; the standalone page wraps them in a document with the
// fit script, and the Full Strategy Report embeds the same section. A second copy of this layout is
// how the report and the print button come to disagree about a farm's election.
// The rules are written unprefixed and SCOPED at use, so the report's own .fes-top/.row/.foot cannot
// reach into the sheet and the sheet cannot reach out.
const SHEET_CSS = `
  .fes-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; border-bottom: 3px solid #1a2e1a; padding-bottom: 8px; }
  .fes-t1 { font-size: 30px; font-weight: 800; letter-spacing: -.01em; }
  .fes-t2 { font-size: 17px; font-weight: 700; color: #2d4a2d; }
  .fes-dl { text-align: right; font-size: 13px; color: #5c6f5c; }
  .fes-dl b { display: block; font-size: 22px; color: #8a3324; font-weight: 800; }
  .fes-rows { margin-top: 10px; }
  .fes-row { break-inside: avoid; display: grid; grid-template-columns: 128px minmax(0, 1fr) 252px; gap: 14px; align-items: start;
         padding: 13px 0; border-bottom: 1px solid #e3ebe3; }
  .fes-cn { font-size: 21px; font-weight: 800; }
  .fes-cc { font-size: 12px; color: #6b7a6b; }
  .fes-opt { font-size: 17px; line-height: 1.3; color: #33402f; }
  .fes-opt + .fes-opt { margin-top: 3px; }
  .fes-opt b { font-size: 20px; font-weight: 800; color: #1f5c2e; }
  .fes-opt span { color: #33402f; }
  .fes-sub { font-size: 13px; color: #6b7a6b; margin-top: 3px; }
  .fes-by { color: #6b7a6b; font-weight: 600; }
  .fes-ref { font-size: 12px; color: #8a3324; margin-top: 4px; }
  .fes-figs { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 12px; font-size: 13px; font-weight: 700; }
  .fes-figs i { display: block; font-style: normal; font-size: 10.5px; font-weight: 600; letter-spacing: .03em;
            text-transform: uppercase; color: #60735f; }
  .fes-tiny { font-size: 10px; font-weight: 600; color: #677167; }
  .fes-next { font-size: 11.5px; color: #8a6d1f; margin-top: 4px; }
  .fes-off, .fes-mism { font-size: 12px; color: #5c6f5c; margin-top: 8px; }
  .fes-mism { color: #8a3324; font-weight: 600; }
  .fes-foot { margin-top: 12px; font-size: 11px; line-height: 1.45; color: #6b7a6b; }
  .fes-auth { display: inline-block; vertical-align: middle; font-size: 10px; font-weight: 800; letter-spacing: .03em;
          text-transform: uppercase; color: #6b4f12; background: #fdf3dc; border: 1px solid #e6c98a;
          border-radius: 4px; padding: 1px 6px; margin-left: 8px; }`;
// Every selector gets the caller's scope, so the sheet's class names are local to it.
const scopedCss = (scope) => SHEET_CSS.replace(/(^|\})\s*([^{}@]+)\{/g, (m, close, sel) =>
  (close || "") + "\n  " + sel.split(",").map((x) => scope + " " + x.trim()).join(", ") + " {");

// The sheet itself — the markup both surfaces print.
function sheetBody(s) {
  const deadline = s.window ? date(s.window.closes) : "—";
  const scenario = s.countyAuthority !== "fsa";
  // ⚠ ONE FOOTNOTE, NOT A PARAGRAPH PER FIGURE (Kevin's call). Everything the page must not be
  // mistaken for lives here: whose counties these are, what "below the line" really means, that the
  // payment yield and base acres are the grower's own and are not assumed, and that this is modeled.
  const foot = [
    scenario ? "Counties as insured, not your FSA base-acre record" : "Counties from your FSA base-acre record",
    "below the payment-yield line ARC-CO leads over part of the price range, not at every price",
    // ⚠ THE BASIS OF THE CALL IS NAMED, BECAUSE THE CALL IS NOW UNCONDITIONAL. It is made at
    // USDA's own published projected price, which is printed beside every crop on this page —
    // Silas projects nothing of its own. Saying only "not a forecast" beside a recommendation
    // decided at a projection would be true of Silas and misleading about the page.
    "each call is made at USDA's published projected price, shown beside the crop — the season-average price is not settled until the marketing year ends",
    "your FSA PLC payment yield is on your FSA farm record — not your APH; nothing here assumes one",
    "base acres size the dollars, not the choice",
    "ARC-IC, a whole-farm option, is screened per farm separately and is not compared here",
    `a farm that does not elect keeps its prior election and receives no ${esc(s.programYear)} payment`,
    `modeled from FSA/USDA figures${s.fsaAsOf ? " as of " + date(s.fsaAsOf) : ""} — not an election made for you; Silas makes no price forecast of its own`,
    "confirm covered commodities, base acres and FSA farm records before electing",
  ].join(" · ");
  const mism = (s.baseAcreMismatch || []).map((m) => `<div class="fes-mism">⚠ ${esc(cropName(m.crop))}: FSA records place the base acres in ${esc(m.fsa.map(title).join(", "))}${m.notFsa.length ? ` — ${esc(m.notFsa.map(title).join(", "))} ${m.notFsa.length === 1 ? "is" : "are"} analyzed here because the crop is insured there` : ""}.</div>`).join("");
  const off = (s.notCovered || []).length
    ? `<div class="fes-off">Not on this page: ${s.notCovered.map((x) => esc(cropName(x.crop)) + " (" + x.counties.map((c) => esc(title(c))).join(", ") + ") — " + esc(x.reason || "not computed")).join("; ")}.</div>`
    : "";
  return `<div class="fes-top">
    <div>
      <div class="fes-t1">${esc(s.programYear)} ARC/PLC election${scenario ? ` <span class="fes-auth">insured-county scenario</span>` : ""}</div>
      <div class="fes-t2">${esc(s.farmerName || "")}</div>
    </div>
    <div class="fes-dl">Elect at FSA by<b>${deadline}</b></div>
  </div>
  <div class="fes-rows">${s.crops.map(cropRow).join("")}</div>
  ${off}${mism}
  <div class="fes-foot">${foot}.</div>`;
}

// The SAME sheet, as a section the Full Strategy Report embeds beside its own pages.
// ⚠ A NAMED PAGE, BECAUSE THE SHEET IS LANDSCAPE AND THE REPORT IS NOT. The Full Strategy
// Report prints `@page { size: letter; margin: 0.5in }` — 7.5in of printable width — and this
// sheet's content is 10.44in. Embedded with no page rule it renders perfectly ON SCREEN and
// prints CUT OFF down the right-hand side, which no HTML screenshot reveals. A NAMED page
// (CSS Paged Media, Chrome 110+) applies to this section only, so the report's own pages are
// untouched: it is not the bare `@page` override this section must never carry.
export function farmElectionSheetSection(s) {
  return `<style>@page fesLandscape { size: letter landscape; margin: 0.28in 0.25in; }
.fes { page: fesLandscape; }
${scopedCss(".fes")}</style>
<section class="page fes">${sheetBody(s)}</section>`;
}

export function renderFarmElectionSummary(s) {
  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(s.programYear)} ARC/PLC Election — ${esc(s.farmerName || "")}</title>
<style>
  @page { size: letter landscape; margin: 0.28in 0.25in; }
  * { box-sizing: border-box; }
  body { margin: 0; font: 15px/1.35 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2a1f; }
  #pg { width: ${PRINT_W_IN}in; }
  ${scopedCss("#pg")}
</style></head><body><div id="pg">
  ${sheetBody(s)}
</div><div id="pg-end"></div>
<script>
/* ONE PAGE, WHATEVER THE FARM — the page is laid out at a fixed printable width and scaled to the
   printable height, never below ${FIT_MIN} (past that it runs to a second page rather than become
   unreadable). */
(function () {
  var P = document.getElementById("pg"), E = document.getElementById("pg-end");
  if (!P || !E || !("zoom" in P.style)) return;
  var W = ${PRINT_W_IN} * 96, H = ${PRINT_H_IN} * 96, z;
  function wide() {
    return [].filter.call(document.querySelectorAll(".fes-row > div, .fes-foot, .fes-top"), function (el) { return el.scrollWidth > el.clientWidth + 1; }).length;
  }
  function set(t) { z = t; P.style.zoom = t; P.style.width = (W / t) + "px"; }
  function fits(t) { set(t); return E.offsetTop <= H && wide() === 0; }
  function narrow(t) { set(t); return wide() === 0; }
  function bisect(ok, a, b) { for (var i = 0; i < 10; i++) { var mid = (a + b) / 2; if (ok(mid)) a = mid; else b = mid; } return a; }
  var lo = ${FIT_MIN}, hi = ${FIT_MAX};
  if (fits(hi)) lo = hi;
  else if (fits(lo)) lo = bisect(fits, lo, hi);
  else lo = narrow(1) ? 1 : bisect(narrow, ${FIT_MIN}, 1);
  set(lo);
  document.documentElement.setAttribute("data-fit-zoom", lo.toFixed(3));
  document.documentElement.setAttribute("data-fit-height", String(E.offsetTop));
  document.documentElement.setAttribute("data-overflow", String(wide()));
})();
</script>
</body></html>`;
}
